import { createContext, useState, useContext, useEffect } from "react";

const identityServiceUrl =
  process.env.REACT_APP_IDENTITY_SERVICE_URL ||
  "https://identity.dev.fsgapps.com";

export interface User {
  id: string;
  name: string;
  username: string;
  email: string;
  employeeNumber: string;
  division: number;
  jobTitle: string;
  jobCode?: string;
  picture?: string;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

type AuthContextType = {
  user: User | null;
  loading: boolean;
  error: string | null;
  picture?: string | null;
  login: (returnUrl?: string) => void;
  logout: (returnUrl?: string) => void;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${identityServiceUrl}/me`, {
        credentials: "include",
        mode: "cors",
      });
      if (!response.ok) {
        if (response.status === 401) {
          setUser(null);
          return;
        }
        throw new Error("Failed to fetch user");
      }
      const userData = await response.json();
      setUser(userData);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const login = (returnUrl = window.location.origin) => {
    console.log("login", returnUrl);
    window.location.href = `${identityServiceUrl}/login?return_url=${encodeURIComponent(
      returnUrl
    )}`;
  };

  const logout = (returnUrl = window.location.origin) => {
    console.log(encodeURIComponent(returnUrl));
    window.location.href = `${identityServiceUrl}/logout?return_url=${encodeURIComponent(
      returnUrl
    )}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return auth;
}
